/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";

import { PubSubReviewRequest, ReviewRequest, FirestoreReviewRequest } from "./review-request";
import { getQueueDisplayName } from "./mapping";

import "@material/data-table";

import "@material/mwc-button";
import "@material/mwc-icon-button";

//import { until } from "lit/directives/until";

import "../style.scss";

@customElement("hitl-batch")
export class Batch extends LitElement {
  static override styles = css`
    .mdc-card > div {
      margin: 0px 12px 0 12px;
    }

    .mdc-card__actions {
      display: flex;
      align-items: center;
      margin-top: 12px;
      gap: 12px;
    }

    .mdc-data-table {
      width: 100%;
    }
  `;

  @property({ type: Array })
  reviewRequests: ReviewRequest[] = [];

  // Computed property for sorted review requests (FIFO - oldest first)
  get sortedReviewRequests(): ReviewRequest[] {
    return [...this.reviewRequests].sort((a, b) => {
      // Sort by createTime ascending (oldest first = longest duration first)
      return a.createTime.getTime() - b.createTime.getTime();
    });
  }

  private _timerID?: number;

  constructor() {
    super();
  }

  override render() {
    return html`
      <link
        href="https://unpkg.com/material-components-web@latest/dist/material-components-web.min.css"
        rel="stylesheet"
      />
      <div class="mdc-card">
        <div class="card-header">
          <div class="card-title">
            <h2 class="mdc-typography--headline6">Arbeitsstapel</h2>
          </div>
        </div>
        <div class="card-body mdc-typography--body2">
          <div class="mdc-data-table">
            <div class="mdc-data-table__table-container">
              <table class="mdc-data-table__table">
                <thead>
                  <tr class="mdc-data-table__header-row">
                    <th
                        class="mdc-data-table__header-cell"
                        role="columnheader"
                        scope="col"
                    >
                      GB
                    </th>
                    <th
                        class="mdc-data-table__header-cell"
                        role="columnheader"
                        scope="col"
                    >
                      Sendung
                    </th>
                    <th
                        class="mdc-data-table__header-cell"
                        role="columnheader"
                        scope="col"
                    >
                      Dokument
                    </th>
                    <th
                        class="mdc-data-table__header-cell"
                        role="columnheader"
                        scope="col"
                    >
                      Eingangskanal
                    </th>
                    <th
                        class="mdc-data-table__header-cell"
                        role="columnheader"
                        scope="col"
                    >
                      Im Arbeitsvorrat seit
                    </th>
                    <th
                        class="mdc-data-table__header-cell"
                        role="columnheader"
                        scope="col"
                    >
                      Zu prüfen
                    </th>
                    <th
                        class="mdc-data-table__header-cell"
                        role="columnheader"
                        scope="col"
                    >
                      Entfernen
                    </th>
                  </tr>
                </thead>
                <tbody class="mdc-data-table__content">
                  ${repeat(
                    this.sortedReviewRequests,
                    (reviewRequest) => reviewRequest.id(),
                    (reviewRequest, _index) => {
                      // Find the original index in the unsorted array for the remove button
                      const originalIndex = this.reviewRequests.indexOf(reviewRequest);
                      return html`
                        <tr class="mdc-data-table__row">
                          <td class="mdc-data-table__cell">
                            ${this.getBusinessArea(reviewRequest)}
                          </td>
                          <td class="mdc-data-table__cell">
                            ${this.getEnvelopeId(reviewRequest)}
                          </td>
                          <td class="mdc-data-table__cell">
                            ${this.getDocumentId(reviewRequest)}
                          </td>
                          <td class="mdc-data-table__cell">
                            ${this.getSource(reviewRequest)}
                          </td>
                          <td class="mdc-data-table__cell">
                            ${reviewRequest.getEnqueueDuration()}
                          </td>
                          <td class="mdc-data-table__cell">
                            ${(() => {

                              // Direct mapping for now to test
                              if (reviewRequest.queue === 'hitl-classification-business-area') {
                                return 'Intelligente Weiche';
                              } else if (reviewRequest.queue === 'hitl-classification-rb') {
                                return 'Dokumentenklassifikation RB';
                              } else if (reviewRequest.queue === 'hitl-data-extraction-rb') {
                                return 'Datenextraktion RB';
                              }

                              const displayName = getQueueDisplayName(reviewRequest.queue);
                              console.log('DEBUG queue display - queue:', reviewRequest.queue, 'displayName:', displayName);
                              return displayName;
                            })()}
                          </td>
                          <td class="mdc-data-table__cell">
                            <mwc-icon-button
                              icon="close"
                              data-index=${originalIndex}
                              @click=${this.removeReviewRequestFromBatch}
                            ></mwc-icon-button>
                          </td>
                        </tr>
                      `;
                    }
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="mdc-card__actions">
          <mwc-button
            raised
            @click=${this.reviewBatch}
            label="Arbeitsstapel prüfen"
            ?disabled=${this.reviewRequests.length === 0}
          ></mwc-button>
        </div>
      </div>
    `;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // Update the durations every 5 seconds to keep them current (reduced frequency for better performance)
    this._timerID = window.setInterval(() => {
      this.requestUpdate();
    }, 5000);
  }

  override disconnectedCallback(): void {
    window.clearInterval(this._timerID);
    this._timerID = undefined;
  }

  async getAccessToken(refresh = false) {
    const [accessToken, idToken] = await new Promise<string>((resolve, reject) => {
      const getAccessToken = new CustomEvent("get-access-token", {
        bubbles: true,
        detail: { refresh: refresh, resolve: resolve, reject: reject },
      });
      this.dispatchEvent(getAccessToken);
    });
    return [accessToken, idToken] as const;
  }

  async removeReviewRequestFromBatch(e: Event) {
    // ensure that the access token gets refreshed if necessary
    await this.getAccessToken(true);
    const indexToRemove = parseInt(
      (e.target as Element).getAttribute("data-index")!
    );
    const reviewRequestToRemove = this.reviewRequests[indexToRemove];

    console.log('Removing review request from batch:', reviewRequestToRemove);

    // Handle task release for different types of review requests
    if (reviewRequestToRemove) {
      if (reviewRequestToRemove instanceof PubSubReviewRequest && reviewRequestToRemove.extendAckIdWorker) {
        // For PubSub requests: terminate the ack extension worker
        reviewRequestToRemove.extendAckIdWorker.terminate();
      } else if (reviewRequestToRemove instanceof FirestoreReviewRequest) {
        // For Firestore requests: try to release the task assignment back to available pool
        // But always emit the event and remove from UI even if server release fails
        try {
          await this.releaseFirestoreTask(reviewRequestToRemove);
          console.log(`Released Firestore task ${reviewRequestToRemove.taskId} from queue ${reviewRequestToRemove.queue}`);
        } catch (error) {
          console.warn('Failed to release Firestore task on server (likely permissions issue), but removing from local UI:', error);
          // Continue anyway - the UI should still update even if server release fails
        }

        // Always emit event to trigger queue count update, regardless of server release success
        const queueUpdateEvent = new CustomEvent("task-released", {
          bubbles: true,
          detail: {
            queueName: reviewRequestToRemove.queue,
            taskId: reviewRequestToRemove.taskId
          }
        });
        this.dispatchEvent(queueUpdateEvent);
      }
    }
    this.reviewRequests = this.reviewRequests.filter(
      (_, i) => i !== indexToRemove
    );
    const reviewRequestUpdatedEvent = new CustomEvent(
      "review-requests-updated",
      {
        bubbles: true,
        detail: this.reviewRequests,
      }
    );
    this.dispatchEvent(reviewRequestUpdatedEvent);
  }

  async reviewBatch() {
    // ensure that the access token gets refreshed if necessary
    const [accessToken, idToken] = await this.getAccessToken(true);

    // Start background preloading of Document AI JSON content for better UX
    this.preloadDocumentAIContent(accessToken);

    const reviewRequestUpdatedEvent = new CustomEvent(
      "batch-review-triggered",
      {
        bubbles: true,
        detail: this.sortedReviewRequests, // Use FIFO sorted order for review
      }
    );
    this.dispatchEvent(reviewRequestUpdatedEvent);
  }

  private async preloadDocumentAIContent(accessToken: string) {
    const sortedRequests = this.sortedReviewRequests;

    // Preload Document AI JSON for the first 3 documents in parallel
    // This reduces wait time when user starts reviewing
    const contentPromises = sortedRequests.slice(0, 3).map(async (reviewRequest, index) => {
      try {
        console.log(`Preloading Document AI content for document ${index + 1}/${Math.min(3, sortedRequests.length)}`);

        // Only preload if content hasn't been cached yet
        if (reviewRequest instanceof FirestoreReviewRequest && !reviewRequest._preloadedContent) {
          const content = await reviewRequest.getContent(accessToken);
          reviewRequest._preloadedContent = content;
          console.log(`Successfully preloaded content for document ${index + 1}`);
          return reviewRequest;
        }
        return null;
      } catch (error) {
        console.warn(`Failed to preload content for document ${index + 1}:`, error);
        return null;
      }
    });

    // Parallel image preloading for documents with content
    const imagePromises = sortedRequests.slice(0, 3).map(async (reviewRequest, index) => {
      try {
        if (reviewRequest instanceof FirestoreReviewRequest && reviewRequest.pageImageUrls.length > 0) {
          console.log(`Starting image preloading for document ${index + 1}`);
          return FirestoreReviewRequest.preloadImages(reviewRequest.pageImageUrls, accessToken);
        }
        return null;
      } catch (error) {
        console.warn(`Failed to preload images for document ${index + 1}:`, error);
        return null;
      }
    });

    // Execute content and image preloading in parallel for better performance
    Promise.all([
      Promise.allSettled(contentPromises),
      Promise.allSettled(imagePromises)
    ]).then(([contentResults, imageResults]) => {
      const successfulContent = contentResults.filter(r => r.status === 'fulfilled').length;
      const successfulImages = imageResults.filter(r => r.status === 'fulfilled').length;
      console.log(`Preloading completed: ${successfulContent}/3 content, ${successfulImages}/3 images`);
    }).catch((error) => {
      console.warn('Document preloading failed:', error);
    });
  }

  private getBusinessArea(reviewRequest: ReviewRequest): string {
    if (reviewRequest instanceof FirestoreReviewRequest) {
      // Return cached business area if already computed
      if (reviewRequest._cachedBusinessArea !== undefined) {
        return reviewRequest._cachedBusinessArea;
      }

      // For Firestore, try to get from multiple possible locations
      const businessArea = reviewRequest.data?.business_area ||
                          reviewRequest.data?.documentData?.metadata?.business_area ||
                          reviewRequest.data?.metadata?.business_area ||
                          "-";

      // Cache the result to prevent repeated computation
      reviewRequest._cachedBusinessArea = businessArea;

      return businessArea;
    }
    return reviewRequest.data?.business_area ?? "-";
  }

  private getEnvelopeId(reviewRequest: ReviewRequest): string {
    if (reviewRequest instanceof FirestoreReviewRequest) {
      // For Firestore, use envelopeId from the task data
      return reviewRequest.envelopeId || reviewRequest.name || "-";
    }
    return reviewRequest.name.split('/')[0];
  }

  private getDocumentId(reviewRequest: ReviewRequest): string {
    if (reviewRequest instanceof FirestoreReviewRequest) {
      // For Firestore, only check for actual document_id values from processed data
      // Do NOT use envelope_id or other fallbacks that aren't real document_ids
      const processedDataDocId = reviewRequest.data?.documentData?.processed_data?.document_id;
      const splittingResultDocId = reviewRequest.data?.documentData?.processed_data?.splittingResult?.[0]?.document_id;

      // Only return actual document_id values, not envelope_id fallbacks
      // For classification tasks before splitting, document_id will be null/undefined, so return "-"
      if (processedDataDocId && processedDataDocId !== null) {
        return processedDataDocId;
      }
      if (splittingResultDocId && splittingResultDocId !== null) {
        return splittingResultDocId;
      }

      // Check if any of the data locations have a real document_id (not envelope_id)
      const dataDocId = reviewRequest.data?.document_id || reviewRequest.data?.documentData?.document_id;
      if (dataDocId && dataDocId !== null &&
          // Make sure it's not an envelope_id by checking it doesn't contain typical envelope patterns
          !dataDocId.includes('_rs-betrieb_') && !dataDocId.includes('scan')) {
        return dataDocId;
      }

      // For classification tasks, there's no document_id yet, so show nothing
      return "-";
    }
    return reviewRequest.data?.document_id ?? "-";
  }

  private getSource(reviewRequest: ReviewRequest): string {
    if (reviewRequest instanceof FirestoreReviewRequest) {
      // Return cached source if already computed
      if (reviewRequest._cachedSource !== undefined) {
        return reviewRequest._cachedSource;
      }

      // Debug logging to understand data structure
      console.log('DEBUG getSource - Firestore task data structure:', {
        taskId: reviewRequest.taskId,
        topLevelSource: reviewRequest.data?.source,
        documentDataSource: reviewRequest.data?.documentData?.source,
        processedDataSource: reviewRequest.data?.documentData?.processed_data?.source,
        fullDocumentData: reviewRequest.data?.documentData,
        fullProcessedData: reviewRequest.data?.documentData?.processed_data
      });

      // For Firestore, try multiple possible locations for source information
      const source = reviewRequest.data?.source ||
                    reviewRequest.data?.documentData?.processed_data?.source ||
                    reviewRequest.data?.documentData?.source ||
                    "-";

      console.log('DEBUG getSource - Final source value:', source);

      // Cache the result to prevent repeated computation
      reviewRequest._cachedSource = source;

      return source;
    }
    return reviewRequest.data?.source ?? "-";
  }

  private async releaseFirestoreTask(reviewRequest: FirestoreReviewRequest): Promise<void> {
    try {
      const [accessToken, idToken] = await this.getAccessToken(true); // Force refresh to ensure we have valid token

      const collectionName = `hitl_tasks_${reviewRequest.queue}`;
      const taskDocUrl = `${process.env.INTERNAL_API_URL}/documents/${collectionName}/${reviewRequest.taskId}`;

      // First, let's check if the document exists and what its current state is
      console.log(`Checking current state of task ${reviewRequest.taskId} before release...`);
      const getResponse = await fetch(taskDocUrl, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      if (!getResponse.ok) {
        console.error(`Cannot read task ${reviewRequest.taskId}:`, getResponse.status, getResponse.statusText);
        throw new Error(`Cannot read task before release: ${getResponse.status}`);
      }

      const currentDoc = await getResponse.json();
      console.log(`Current task state:`, {
        status: currentDoc.status,
        assignedTo: currentDoc.assignedTo,
        taskId: reviewRequest.taskId
      });

      const updatePayload = {
        status: 'available',
        assignedTo: null,
        assignedAt: null,
        expiresAt: null
      };

      const updateResponse = await fetch(taskDocUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });

      if (updateResponse.ok) {
        console.log(`Released Firestore task ${reviewRequest.taskId} back to available pool`);
      } else {
        // Get the response body for more detailed error information
        const errorBody = await updateResponse.text();
        console.error(`PATCH ${taskDocUrl} failed:`, {
          status: updateResponse.status,
          statusText: updateResponse.statusText,
          body: errorBody,
          headers: Object.fromEntries(updateResponse.headers.entries())
        });
        throw new Error(`Failed to release task: ${updateResponse.status} ${updateResponse.statusText} - ${errorBody}`);
      }
    } catch (error) {
      console.error('Error releasing Firestore task:', error);
      throw error;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "hitl-batch": Batch;
  }
}
